import React from 'react';

import Dropdown from 'components/cosmos/dropdown';
import { PageBadgeGroup } from 'components/moduleList/moduleItem/modulePageList/modulePage/pageBadgeGroup';
import { ReactComponent as ArrowUpRight } from 'components/svg/icons/arrow-up-right.svg';
import { ReactComponent as CloseCosmos } from 'components/svg/icons/close-cosmos.svg';
import { ReactComponent as DuplicateCosmos } from 'components/svg/icons/duplicate-sm.svg';
import { ReactComponent as EyeCosmos } from 'components/svg/icons/eye-cosmos.svg';
import { ReactComponent as PenToSquare } from 'components/svg/icons/pen-to-square.svg';
import { ReactComponent as Trash } from 'components/svg/icons/trash.svg';
import { I18n } from 'components/translate';
import { useTranslation } from 'configs/translationContext';
import { LIBERATION_TYPES } from 'helpers/Dripping';
import { inject } from 'mobx-react';
import { PAGE_TYPE as PAGE_TYPE_WEBINAR } from 'modules/content/components/webinar/WebinarService';
import { FeatureToggles } from 'modules/featureToggle';
import PropTypes from 'prop-types';

import { SCHEDULER_OPTIONS } from '../scheduler/constants';

import './PageInfoMobile.scss';

const NONE_SYMBOL = '\u2014';

const Row = React.memo(({ label, value }) => {
  return (
    <div className="_text-1 _text-gray-500">
      <p className="_font-weight-bold _mb-0 _mt-0">
        <I18n label={label} />
      </p>
      <p className="_mb-0 _mt-0">{value || NONE_SYMBOL}</p>
    </div>
  );
});

Row.propTypes = {
  icon: PropTypes.string,
  label: PropTypes.string,
  value: PropTypes.string
};

const RowActions = React.memo(
  ({
    onClick,
    icon = null,
    label = '',
    labelClassName = '',
    postIcon = null
  }) => (
    <button
      className="_d-flex _justify-content-between _align-items-center _w-full"
      onClick={onClick}
    >
      <div className="_d-flex _align-items-center">
        {icon}
        <p className={`${labelClassName} _mb-0 _mt-0 _text-2 _text-gray-700`}>
          <I18n label={label} />
        </p>
      </div>
      {postIcon}
    </button>
  )
);

RowActions.propTypes = {
  icon: PropTypes.node,
  label: PropTypes.string,
  labelClassName: PropTypes.string,
  onClick: PropTypes.func,
  postIcon: PropTypes.node
};

const DrippingInfo = React.memo(({ item, type }) => {
  const drippingInfo = SCHEDULER_OPTIONS[type];
  const data = drippingInfo.value(item);

  if (!data.count) {
    return NONE_SYMBOL;
  }

  return <I18n label={drippingInfo.label} data={data} />;
});

DrippingInfo.propTypes = { item: PropTypes.object, type: PropTypes.string };

const PageInfoMobile = ({ item, actions, isDrippingByClassEnabled }) => {
  const { t } = useTranslation();
  const { type, totalComments, avgRate, liberationDate, drippingConfigsSize } =
    item;
  const isWebinar = type === PAGE_TYPE_WEBINAR;
  const visualizeInfo =
    type === PAGE_TYPE_WEBINAR
      ? { label: 'general.panel', action: actions.webinar }
      : { label: 'general.visualize', action: actions.visualize };

  const handleEdit = (action) => {
    action?.();
  };

  const renderDrippingInformation = () => {
    if (isDrippingByClassEnabled) {
      if (drippingConfigsSize) {
        return (
          <Dropdown.MenuItem className="_pb-0 _pl-5 _pr-5">
            <Row
              label="dripping_2.new.release_and_expiration.title"
              value={t('dripping_2.amount', {
                amountToPluralize: drippingConfigsSize,
                data: { value: drippingConfigsSize }
              })}
            />
          </Dropdown.MenuItem>
        );
      }

      return null;
    }

    return (
      <>
        <Dropdown.MenuItem className="_pb-0 _pl-5 _pr-5">
          <Row
            data-test="page-info-dripping-row"
            label="general.release_in"
            value={
              liberationDate ? (
                SCHEDULER_OPTIONS[LIBERATION_TYPES.BY_DATE].value(item)
              ) : (
                <DrippingInfo item={item} type={LIBERATION_TYPES.BY_DAYS} />
              )
            }
          />
        </Dropdown.MenuItem>
        <Dropdown.MenuItem className="_pb-0 _pl-5 _pr-5">
          <Row
            data-test="page-info-expires-row"
            label="general.expires_in"
            value={
              <DrippingInfo item={item} type={LIBERATION_TYPES.EXPIRATION} />
            }
          />
        </Dropdown.MenuItem>
      </>
    );
  };

  return (
    <>
      <Dropdown.MenuHeader className="_pt-5 _pl-5 _pr-5">
        <span className="_d-flex _justify-content-between">
          <h4
            className="dropdown-header _text-3 _text-gray-700 _mb-0"
            data-test="page-info-title"
          >
            {item.name}
          </h4>
          <CloseCosmos data-test="close-icon" onClick={Dropdown.closeMenu} />
        </span>
        <PageBadgeGroup
          className="_mt-3"
          pageType={type}
          isPublished={item.published}
        />
      </Dropdown.MenuHeader>
      {renderDrippingInformation()}
      <Dropdown.MenuItem className="_pb-0 _pl-5 _pr-5">
        <Row
          data-test="page-info-comments-row"
          label="general.comments_total"
          value={totalComments}
        />
      </Dropdown.MenuItem>
      <Dropdown.MenuItem className="_pl-5 _pr-5">
        <Row
          data-test="page-info-avg-row"
          label="general.avg_grade"
          value={avgRate}
        />
      </Dropdown.MenuItem>

      <Dropdown.MenuDivider className="_ml-5 _mr-5" />

      <Dropdown.MenuItem
        className="_pl-5 _pr-5 _pt-4 _pb_4"
        data-test="page-info-visualize"
        onClick={visualizeInfo.action}
      >
        <RowActions
          label={visualizeInfo.label}
          icon={<EyeCosmos className="page-info__icon page-info__icon__eye" />}
          postIcon={
            <ArrowUpRight className="page-info__icon page-info__icon__arrow-up" />
          }
          labelClassName="_ml-4"
        />
      </Dropdown.MenuItem>
      <Dropdown.MenuItem
        className="_pl-5 _pr-5 _pt-4 _pb_4"
        data-test="page-info-edit"
        onClick={() => handleEdit(actions.edit)}
      >
        <RowActions
          label="general.edit"
          icon={
            <PenToSquare className="page-info__icon page-info__icon__pen-to-square" />
          }
          postIcon={null}
          labelClassName="_ml-4"
        />
      </Dropdown.MenuItem>
      {!isWebinar && (
        <Dropdown.MenuItem
          className="_pl-5 _pr-5 _pt-4 _pb_4"
          onClick={actions.duplicate}
        >
          <RowActions
            label="general.duplicate"
            icon={
              <DuplicateCosmos className="page-info__icon page-info__icon__duplicate _ml-1" />
            }
            postIcon={null}
            labelClassName="_ml-3"
          />
        </Dropdown.MenuItem>
      )}
      <Dropdown.MenuItem
        className="_pl-5 _pr-5 _pt-4 _pb_4"
        data-test="page-info-delete"
        onClick={actions.delete}
      >
        <RowActions
          label="general.remove"
          icon={
            <Trash className="page-info__icon page-info__icon__trash _ml-1" />
          }
          postIcon={null}
          labelClassName="_ml-3"
        />
      </Dropdown.MenuItem>
    </>
  );
};

PageInfoMobile.propTypes = {
  item: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    type: PropTypes.string,
    liberationDate: PropTypes.string,
    totalComments: PropTypes.number,
    avgRate: PropTypes.number
  }),
  actions: PropTypes.shape({
    delete: PropTypes.func,
    edit: PropTypes.func,
    visualize: PropTypes.func,
    webinar: PropTypes.fuc,
    duplicate: PropTypes.func
  })
};

PageInfoMobile.defaultProps = {
  item: {},
  actions: {}
};

export default inject((stores) => ({
  isDrippingByClassEnabled: stores.FeatureToggleStore.featureToggles.get(
    FeatureToggles.DrippingByClass
  )
}))(PageInfoMobile);
